// Profil-Nachbau F-07, F-08 (output/profil-team-20261014/BAU-COCKPIT.md 1.1 „Zeitraum“, „Reiterleiste“, 0.4): ein Zeitraum für
// alle Teile des KI-Cockpits, gemerkt in der Adresse (zeitraum=… oder von=…&bis=…; ohne Angabe „Letzte 30 Tage“), dazu der
// offene Teil (reiter=…). Die Seite setzt ihre Abfrageteile immer zusammen mit den vorhandenen (ansicht, organisation …), nie an
// deren Stelle. Der Server löst die Vorgaben auf (eine Wahrheit für „heute“, backend/src/administration/cockpit/zeitraum.ts);
// hier stehen Lesen und Schreiben der Adresse, die Prüfung des eigenen Zeitraums am Feld und der Satz „Angezeigt: …“.
// Profil-Nachbau F-25 (PF-GR-cockpit-2, SPEC cockpit-grafiken 2.3): dazu das Monatsraster, die Klickfolge und die Tasten des Kalenders.
import { ZEITRAUM } from './cockpitTexte';
import { datum } from './format';

export const VORGABEN = ['heute', 'letzte-7-tage', 'letzte-14-tage', 'letzte-30-tage', 'dieser-monat', 'letzter-monat', 'dieses-jahr'] as const;
export type Vorgabe = (typeof VORGABEN)[number];
export const VORGABE: Vorgabe = 'letzte-30-tage';
export const HOECHSTENS_TAGE = 366;
/** Die Wahl in der Adresse. */
export type ZeitraumWahl = { art: 'VORGABE'; vorgabe: Vorgabe } | { art: 'EIGEN'; von: string; bis: string };
/** Der aufgelöste Zeitraum aus der Antwort des Servers (Tage als JJJJ-MM-TT). */
export type Zeitraum = { von: string; bis: string; tage: number; vorgabe: Vorgabe | null };

const TAG_MS = 86400000;
const ZEITRAUM_TEILE = ['zeitraum', 'von', 'bis'];
const alsZahl = (tag: string) => Date.UTC(Number(tag.slice(0, 4)), Number(tag.slice(5, 7)) - 1, Number(tag.slice(8, 10)));
const plusTage = (tag: string, n: number) => new Date(alsZahl(tag) + n * TAG_MS).toISOString().slice(0, 10);
/** Ein echter Tag als JJJJ-MM-TT (kein 31.02.). */
const echt = (tag: string) => /^\d{4}-\d{2}-\d{2}$/.test(tag) && new Date(alsZahl(tag)).toISOString().slice(0, 10) === tag;
const istVorgabe = (wert: string | null): wert is Vorgabe => wert !== null && (VORGABEN as readonly string[]).includes(wert);
export const tageZwischen = (von: string, bis: string) => Math.round((alsZahl(bis) - alsZahl(von)) / TAG_MS) + 1;

/** Der heutige Tag in Europe/Vienna als JJJJ-MM-TT (wie am Server). */
export function heuteInWien(jetzt: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Vienna', year: 'numeric', month: '2-digit', day: '2-digit' }).format(jetzt);
}

// ---- Adresse

/** Ein eigener Zeitraum braucht beide Tage, echt; eine unbekannte Vorgabe gilt als „Letzte 30 Tage“. Ob der eigene Zeitraum
 *  in der Grenze liegt, sagt der Server (400 mit dem Satz für das Feld). */
export function zeitraumLesen(params: URLSearchParams): ZeitraumWahl {
  const von = params.get('von'), bis = params.get('bis');
  if (von && bis && echt(von) && echt(bis)) return { art: 'EIGEN', von, bis };
  const vorgabe = params.get('zeitraum');
  return { art: 'VORGABE', vorgabe: istVorgabe(vorgabe) ? vorgabe : VORGABE };
}
/** Die Abfrage mit dem neuen Zeitraum; alle anderen Teile bleiben. „Letzte 30 Tage“ steht ohne Angabe. */
export function zeitraumSetzen(params: URLSearchParams, wahl: ZeitraumWahl): URLSearchParams {
  const neu = new URLSearchParams(params);
  for (const teil of ZEITRAUM_TEILE) neu.delete(teil);
  if (wahl.art === 'EIGEN') { neu.set('von', wahl.von); neu.set('bis', wahl.bis); }
  else if (wahl.vorgabe !== VORGABE) neu.set('zeitraum', wahl.vorgabe);
  return neu;
}
/** Der offene Teil aus der Adresse; ein unbekannter oder hier nicht erlaubter Teil gilt als vorgabe. */
export function reiterLesen<T extends string>(params: URLSearchParams, erlaubt: readonly T[], vorgabe: T): T {
  const reiter = params.get('reiter');
  return erlaubt.find(wert => wert === reiter) ?? vorgabe;
}
/** Die Abfrage mit dem neuen Teil; Zeitraum und alle anderen Teile bleiben. Die Vorgabe steht ohne Angabe. */
export function reiterSetzen(params: URLSearchParams, reiter: string, vorgabe: string): URLSearchParams {
  const neu = new URLSearchParams(params);
  if (reiter === vorgabe) neu.delete('reiter'); else neu.set('reiter', reiter);
  return neu;
}
/** Abfrageteil für die Wege des Servers. */
export const zeitraumAbfrage = (wahl: ZeitraumWahl) => wahl.art === 'EIGEN'
  ? `von=${encodeURIComponent(wahl.von)}&bis=${encodeURIComponent(wahl.bis)}` : `zeitraum=${wahl.vorgabe}`;
/** Teil des Körpers für den Export. */
export const zeitraumKoerper = (wahl: ZeitraumWahl): { zeitraum: Vorgabe } | { von: string; bis: string } => wahl.art === 'EIGEN'
  ? { von: wahl.von, bis: wahl.bis } : { zeitraum: wahl.vorgabe };

// ---- Eigener Zeitraum

/** TT.MM.JJJJ (auch T.M.JJJJ) → JJJJ-MM-TT; null ohne echten Tag. */
export function datumLesen(text: string): string | null {
  const teile = /^\s*(\d{1,2})\.(\d{1,2})\.(\d{4})\s*$/.exec(text);
  if (!teile) return null;
  const tag = `${teile[3]}-${teile[2].padStart(2, '0')}-${teile[1].padStart(2, '0')}`;
  return echt(tag) ? tag : null;
}
export type EigenerZeitraum = { ok: true; von: string; bis: string } | { ok: false; fehler: { von?: string; bis?: string } };
/** Prüfung der Felder „Von“ und „Bis“ mit den Sätzen am Feld: Form, Reihenfolge, höchstens 366 Tage bis heute. */
export function eigenerZeitraum(vonText: string, bisText: string, heute: string): EigenerZeitraum {
  const von = datumLesen(vonText), bis = datumLesen(bisText);
  if (!von || !bis) return { ok: false, fehler: { ...(von ? {} : { von: ZEITRAUM.format }), ...(bis ? {} : { bis: ZEITRAUM.format }) } };
  if (von > bis) return { ok: false, fehler: { bis: ZEITRAUM.reihenfolge } };
  if (bis > heute) return { ok: false, fehler: { bis: ZEITRAUM.laenge } };
  if (tageZwischen(von, bis) > HOECHSTENS_TAGE) return { ok: false, fehler: { von: ZEITRAUM.laenge } };
  return { ok: true, von, bis };
}
/** „Angezeigt: 15.09.2026 bis 14.10.2026 · 30 Tage · österreichische Zeit“ aus der Antwort des Servers. */
export const angezeigtText = (z: Pick<Zeitraum, 'von' | 'bis' | 'tage'>) => ZEITRAUM.angezeigt(datum(z.von), datum(z.bis), z.tage);

// ---- Kalender (PF-GR-cockpit-2)

/** Tage eines Monats JJJJ-MM (28 bis 31). */
const tageImMonat = (monat: string) => new Date(Date.UTC(Number(monat.slice(0, 4)), Number(monat.slice(5, 7)), 0)).getUTCDate();

/** Montag = 0 … Sonntag = 6 eines Tags JJJJ-MM-TT. */
export const wochentag = (tag: string) => (new Date(alsZahl(tag)).getUTCDay() + 6) % 7;
/** JJJJ-MM plus n Monate. */
export function monatPlus(monat: string, n: number): string {
  const stelle = Number(monat.slice(0, 4)) * 12 + Number(monat.slice(5, 7)) - 1 + n, jahr = Math.floor(stelle / 12);
  return `${jahr}-${String(stelle - jahr * 12 + 1).padStart(2, '0')}`;
}
/** Beginn und Ende einer Vorgabe am Tag heute (Wien), gleich dem Server (cockpit/zeitraum.ts ausVorgabe). */
export function vorgabeTage(vorgabe: Vorgabe, heute: string): { von: string; bis: string } {
  switch (vorgabe) {
    case 'heute': return { von: heute, bis: heute };
    case 'letzte-7-tage': return { von: plusTage(heute, -6), bis: heute };
    case 'letzte-14-tage': return { von: plusTage(heute, -13), bis: heute };
    case 'letzte-30-tage': return { von: plusTage(heute, -29), bis: heute };
    case 'dieser-monat': return { von: `${heute.slice(0, 7)}-01`, bis: heute };
    case 'letzter-monat': { const ende = plusTage(`${heute.slice(0, 7)}-01`, -1); return { von: `${ende.slice(0, 7)}-01`, bis: ende }; }
    case 'dieses-jahr': return { von: `${heute.slice(0, 4)}-01-01`, bis: heute };
  }
}
/** Die Tage einer Wahl, mit denen der Kalender öffnet (Vorgabe aufgelöst, eigener Zeitraum wie gewählt). */
export function wahlTage(wahl: ZeitraumWahl, heute: string): { von: string; bis: string } {
  return wahl.art === 'EIGEN' ? { von: wahl.von, bis: wahl.bis } : vorgabeTage(wahl.vorgabe, heute);
}
/** Wählbar sind heute − 365 bis heute (höchstens 366 Tage, wie eigenerZeitraum und der Server). */
export function kalenderGrenzen(heute: string): { min: string; max: string } {
  return { min: plusTage(heute, -(HOECHSTENS_TAGE - 1)), max: heute };
}
/** Wochen eines Monats JJJJ-MM als Zeilen Montag bis Sonntag; Tage außerhalb des Monats null. */
export function monatsRaster(monat: string): (string | null)[][] {
  const felder: (string | null)[] = Array.from({ length: wochentag(`${monat}-01`) }, () => null);
  for (let tag = 1; tag <= tageImMonat(monat); tag++) felder.push(`${monat}-${String(tag).padStart(2, '0')}`);
  while (felder.length % 7) felder.push(null);
  const wochen: (string | null)[][] = [];
  for (let i = 0; i < felder.length; i += 7) wochen.push(felder.slice(i, i + 7));
  return wochen;
}
export type KalenderStand = { anfang: string; ende: string | null; wartet: boolean };
/** Erster Klick setzt den Beginn (ende null, wartet), der zweite das Ende; liegt er davor, werden beide getauscht. */
export function kalenderKlick(stand: KalenderStand, tag: string): KalenderStand {
  if (!stand.wartet) return { anfang: tag, ende: null, wartet: true };
  return tag < stand.anfang ? { anfang: tag, ende: stand.anfang, wartet: false } : { anfang: stand.anfang, ende: tag, wartet: false };
}
/** Neuer Fokus im Monatsraster für eine Taste (ArrowLeft/Right ±1, ArrowUp/Down ±7, Home/End Montag/Sonntag der Woche,
 *  PageUp/PageDown ±1 Monat mit dem Tag geklemmt auf die Monatslänge), geklemmt auf die Grenzen; null bei jeder anderen Taste. */
export function kalenderTaste(tag: string, taste: string, grenzen: { min: string; max: string }): string | null {
  let neu: string;
  switch (taste) {
    case 'ArrowLeft': neu = plusTage(tag, -1); break;
    case 'ArrowRight': neu = plusTage(tag, 1); break;
    case 'ArrowUp': neu = plusTage(tag, -7); break;
    case 'ArrowDown': neu = plusTage(tag, 7); break;
    case 'Home': neu = plusTage(tag, -wochentag(tag)); break;
    case 'End': neu = plusTage(tag, 6 - wochentag(tag)); break;
    case 'PageUp':
    case 'PageDown': {
      const monat = monatPlus(tag.slice(0, 7), taste === 'PageUp' ? -1 : 1);
      neu = `${monat}-${String(Math.min(Number(tag.slice(8, 10)), tageImMonat(monat))).padStart(2, '0')}`;
      break;
    }
    default: return null;
  }
  return neu < grenzen.min ? grenzen.min : neu > grenzen.max ? grenzen.max : neu;
}
/** „Letzte 14 Tage“ bzw. „01.10.2026 – 14.10.2026“ (ZEITRAUM.vorgaben, ZEITRAUM.spanne). */
export function wahlName(wahl: ZeitraumWahl): string {
  return wahl.art === 'EIGEN' ? ZEITRAUM.spanne(datum(wahl.von), datum(wahl.bis)) : ZEITRAUM.vorgaben[wahl.vorgabe];
}
/** Eine Spanne, die genau einer Vorgabe entspricht, wird diese Vorgabe (erste in VORGABEN), sonst ein eigener Zeitraum. */
export function wahlAusTagen(von: string, bis: string, heute: string): ZeitraumWahl {
  const vorgabe = VORGABEN.find(v => { const tage = vorgabeTage(v, heute); return tage.von === von && tage.bis === bis; });
  return vorgabe ? { art: 'VORGABE', vorgabe } : { art: 'EIGEN', von, bis };
}
/** „Oktober 2026“, „Jänner 2027“ (ZEITRAUM.monate). */
export function monatTitel(monat: string): string {
  return `${ZEITRAUM.monate[Number(monat.slice(5, 7)) - 1]} ${monat.slice(0, 4)}`;
}
/** Name eines Tags für Vorlesehilfen: „Dienstag, 13. Oktober 2026“, mit Zusätzen „…, Beginn, heute“. */
export function tagName(tag: string, zusaetze: string[] = []): string {
  const name = `${ZEITRAUM.wochentageLang[wochentag(tag)]}, ${Number(tag.slice(8, 10))}. ${ZEITRAUM.monate[Number(tag.slice(5, 7)) - 1]} ${tag.slice(0, 4)}`;
  return [name, ...zusaetze].join(', ');
}
